import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { motion, AnimatePresence } from 'framer-motion';
import './styles/darkmode.css';
import Header from './components/Header';
import FooterNew from './components/FooterNew';
import ScrollToTop from './components/ScrollToTop';
// Sticky contact bar removed - using header buttons instead
import ChatBot from './components/ChatBot';
import HomeModern from './pages/HomeModern';
import './styles/HomeModern.css';
import Inventory from './pages/Inventory';
import VehicleDetail from './pages/VehicleDetail';
// Vehicles page removed - using Inventory instead
import GolfCarts from './pages/GolfCarts';
import GolfCartServices from './pages/GolfCartServices';
import GolfCartRepairService from './pages/GolfCartRepairService';
import Trailers from './pages/Trailers';
import GolfCartDetail from './pages/GolfCartDetail';
import TrailerDetail from './pages/TrailerDetail';
import About from './pages/About';
import Contact from './pages/Contact';
import Financing from './pages/Financing';
import Service from './pages/Service';
import ServicesIndex from './pages/locations/services/ServicesIndex';
// DieselRepair removed — truck content removed per owner
import EuropeanCarRepair from './pages/locations/services/EuropeanCarRepair';
import BMWService from './pages/locations/services/european-car-repair/BMW';
import AudiService from './pages/locations/services/european-car-repair/Audi';
import JaguarService from './pages/locations/services/european-car-repair/Jaguar';
import VolvoService from './pages/locations/services/european-car-repair/Volvo';
import MercedesService from './pages/locations/services/european-car-repair/Mercedes';
import PorscheService from './pages/locations/services/european-car-repair/Porsche';
import VolkswagenService from './pages/locations/services/european-car-repair/Volkswagen';
import VolkswagenInventory from './pages/inventory/VolkswagenInventory';
import BMWInventory from './pages/inventory/BMWInventory';
import AudiInventory from './pages/inventory/AudiInventory';
import MiniCooperService from './pages/locations/services/european-car-repair/MiniCooper';
import AlfaRomeoService from './pages/locations/services/european-car-repair/AlfaRomeo';
import LandRoverService from './pages/locations/services/european-car-repair/LandRover';
import ClutchRepair from './pages/locations/services/ClutchRepair';
import CheckEngineLight from './pages/locations/services/CheckEngineLight';
import WheelService from './pages/locations/services/WheelService';
import TimingChainBeltReplacement from './pages/locations/services/TimingChainBeltReplacement';
import OilChangeService from './pages/locations/services/OilChange';
import DallasEuropeanAutoRepair from './pages/locations/DallasEuropeanAutoRepair';
import DallasBMWRepair from './pages/locations/DallasBMWRepair';
import TradeIn from './pages/TradeIn';
import Blog from './pages/Blog';
import BlogPost from './pages/BlogPost';
import PorschePSMFailure from './pages/blog/PorschePSMFailureFixed';
import VWEPCLight from './pages/blog/VWEPCLightFixed';
import BMWOilChangeJiffyLube from './pages/blog/BMWOilChangeJiffyLube';
import BMWCommonProblems from './pages/blog/BMWCommonProblems';
import PorscheCayenneSuspensionDallas from './pages/blog/PorscheCayenneSuspensionDallas';
import PorscheCayenneLifespan from './pages/blog/PorscheCayenneLifespan';
import LocalSEO from './pages/LocalSEO';
import Admin from './pages/Admin';
import AiTools from './pages/AiTools';
import FinanceApplication from './pages/FinanceApplication';
import NotFound from './pages/NotFound';
import PaymentSuccess from './pages/PaymentSuccess';
import CustomerPaymentPortal from './pages/CustomerPaymentPortal';
import TermsAndConditions from './pages/TermsAndConditions';
import Privacy from './pages/Privacy';
import Warranty from './pages/Warranty';
import RefundPolicy from './pages/RefundPolicy';
import CookiePolicy from './pages/CookiePolicy';
import Disclaimer from './pages/Disclaimer';
// Diesel truck pages removed per owner
import TransmissionRepair from './pages/locations/services/TransmissionRepair';
import SuspensionRepair from './pages/locations/services/SuspensionRepair';
import VehicleDiagnostics from './pages/locations/services/VehicleDiagnostics';
import RadiatorRepair from './pages/locations/services/RadiatorRepair';
import FactoryMaintenance from './pages/locations/services/FactoryMaintenance';
import DrivelineDifferential from './pages/locations/services/DrivelineDifferential';
import PerformanceUpgrades from './pages/locations/services/PerformanceUpgrades';
import RangeRover from './pages/locations/services/european-car-repair/RangeRover';
import './App.css';
import './styles/slick-vars.css';

function App() {
  // Initialize Preline UI
  useEffect(() => {
    // Wait for document to be fully loaded
    const observer = new MutationObserver(() => {
      if (document.readyState === 'complete') {
        if (window.HSStaticMethods) {
          window.HSStaticMethods.autoInit();
        }
        observer.disconnect();
      }
    });
    
    observer.observe(document, { childList: true, subtree: true });
    
    return () => observer.disconnect();
  }, []);

  return (
    <HelmetProvider>
      <Router>
        <ScrollToTop />
        <div className="App min-h-screen bg-gray-50">
          <AnimatePresence mode="wait">
            <Routes>
              <Route path="/admin" element={<Admin />} />
              <Route path="/ADMIN" element={<Admin />} />
              <Route path="/Admin" element={<Admin />} />
              <Route
                path="/*"
                element={
                  <>
                    <Header />
                    <Routes>
                      <Route path="/" element={<HomeModern />} />
                      <Route path="/vehicles" element={<Inventory />} />
                      <Route path="/inventory" element={<Inventory />} />
                      <Route path="/inventory/volkswagen" element={<VolkswagenInventory />} />
                      <Route path="/inventory/bmw" element={<BMWInventory />} />
                      <Route path="/inventory/audi" element={<AudiInventory />} />
                      <Route path="/golf-carts" element={<GolfCarts />} />
                      <Route path="/golf-cart-services" element={<GolfCartServices />} />
                      <Route path="/golf-cart-repair" element={<GolfCartRepairService />} />
                      <Route path="/vehicle/:id" element={<VehicleDetail />} />
                      <Route path="/golf-cart/:id" element={<GolfCartDetail />} />
                      <Route path="/trailer/:id" element={<TrailerDetail />} />
                      <Route path="/trailers" element={<Trailers />} />
                      <Route path="/payment-success" element={<PaymentSuccess />} />
                      <Route path="/my-payments" element={<CustomerPaymentPortal />} />
                      <Route path="/payment-portal" element={<CustomerPaymentPortal />} />
                      <Route path="/about" element={<About />} />
                      <Route path="/contact" element={<Contact />} />
                      <Route path="/warranty" element={<Warranty />} />
                      <Route path="/appointment" element={<Service />} />
                      {/* Redirect old service URL to appointment */}
                      <Route path="/service" element={<Navigate to="/appointment" replace />} />
                      {/* === PRIMARY SERVICE PAGES (CANONICAL URLs - Option 1 Structure) === */}
                      <Route path="/services" element={<ServicesIndex />} />
                      
                      {/* European Car Repair Hub & Brand Pages */}
                      <Route path="/services/european-car-repair" element={<EuropeanCarRepair />} />
                      <Route path="/services/european-car-repair/bmw" element={<BMWService />} />
                      <Route path="/services/european-car-repair/audi" element={<AudiService />} />
                      <Route path="/services/european-car-repair/mercedes" element={<MercedesService />} />
                      <Route path="/services/european-car-repair/porsche" element={<PorscheService />} />
                      <Route path="/services/european-car-repair/jaguar" element={<JaguarService />} />
                      <Route path="/services/european-car-repair/volvo" element={<VolvoService />} />
                      <Route path="/services/european-car-repair/volkswagen" element={<VolkswagenService />} />
                      <Route path="/services/european-car-repair/mini-cooper" element={<MiniCooperService />} />
                      <Route path="/services/european-car-repair/land-rover" element={<LandRoverService />} />
                      <Route path="/services/european-car-repair/alfa-romeo" element={<AlfaRomeoService />} />
                      
                      {/* Diesel/truck pages removed — redirect to services */}
                      <Route path="/services/dallas-diesel-paradise-pros" element={<Navigate to="/services" replace />} />
                      <Route path="/services/dallas-diesel-paradise-pros/*" element={<Navigate to="/services" replace />} />
                      <Route path="/services/diesel/*" element={<Navigate to="/services" replace />} />
                      
                      {/* General Service Pages */}
                      <Route path="/services/transmission-repair" element={<TransmissionRepair />} />
                      <Route path="/services/suspension-repair" element={<SuspensionRepair />} />
                      <Route path="/services/vehicle-diagnostics" element={<VehicleDiagnostics />} />
                      <Route path="/services/radiator-repair" element={<RadiatorRepair />} />
                      <Route path="/services/factory-maintenance" element={<FactoryMaintenance />} />
                      <Route path="/services/driveline-differential" element={<DrivelineDifferential />} />
                      <Route path="/services/performance-upgrades" element={<PerformanceUpgrades />} />
                      
                      {/* Additional Service Pages */}
                      <Route path="/services/timing-chain-belt-replacement" element={<TimingChainBeltReplacement />} />
                      <Route path="/services/clutch-repair" element={<ClutchRepair />} />
                      <Route path="/services/diagnostic-check-engine-light" element={<CheckEngineLight />} />
                      <Route path="/services/wheel-and-tire" element={<WheelService />} />
                      <Route path="/services/oil-change" element={<OilChangeService />} />
                      
                      {/* Legacy URL redirects for old service pages */}
                      <Route path="/services/differential-driveline-repair" element={<Navigate to="/services/driveline-differential" replace />} />
                      <Route path="/services/car-ac-repair" element={<Navigate to="/services" replace />} />
                      
                      {/* View Inventory Redirect */}
                      <Route path="/view-inventory" element={<Navigate to="/inventory" replace />} />
                      
                      {/* === LOCATION-SPECIFIC LANDING PAGES (UNIQUE CONTENT) === */}
                      <Route path="/dallas-european-auto-repair" element={<DallasEuropeanAutoRepair />} />
                      <Route path="/dallas-bmw-repair" element={<DallasBMWRepair />} />
                      
                      {/* === REDIRECTS FROM OLD/DUPLICATE URLs === */}
                      {/* Redirect all /repair/* to /services/* */}
                      <Route path="/repair" element={<Navigate to="/services" replace />} />
                      <Route path="/repair/diesel" element={<Navigate to="/services" replace />} />
                      <Route path="/repair/european-vehicles" element={<Navigate to="/services/european-car-repair" replace />} />
                      <Route path="/repair/european-vehicles/bmw" element={<Navigate to="/services/european-car-repair/bmw" replace />} />
                      <Route path="/repair/european-vehicles/audi" element={<Navigate to="/services/european-car-repair/audi" replace />} />
                      <Route path="/repair/european-vehicles/mercedes" element={<Navigate to="/services/european-car-repair/mercedes" replace />} />
                      <Route path="/repair/european-vehicles/porsche" element={<Navigate to="/services/european-car-repair/porsche" replace />} />
                      <Route path="/repair/european-vehicles/jaguar" element={<Navigate to="/services/european-car-repair/jaguar" replace />} />
                      <Route path="/repair/european-vehicles/volvo" element={<Navigate to="/services/european-car-repair/volvo" replace />} />
                      <Route path="/repair/european-vehicles/volkswagen" element={<Navigate to="/services/european-car-repair/volkswagen" replace />} />
                      <Route path="/repair/european-vehicles/mini-cooper" element={<Navigate to="/services/european-car-repair/mini-cooper" replace />} />
                      <Route path="/repair/european-vehicles/land-rover" element={<Navigate to="/services/european-car-repair/land-rover" replace />} />
                      <Route path="/repair/european-vehicles/alfa-romeo" element={<Navigate to="/services/european-car-repair/alfa-romeo" replace />} />
                      <Route path="/repair/european-vehicles/range-rover" element={<Navigate to="/services/european-car-repair/land-rover" replace />} />
                      
                      {/* Redirect duplicate location pages */}
                      <Route path="/dallas-porsche-repair" element={<Navigate to="/services/european-car-repair/porsche" replace />} />
                      <Route path="/dallas-mercedes-repair" element={<Navigate to="/services/european-car-repair/mercedes" replace />} />
                      <Route path="/dallas-audi-repair" element={<Navigate to="/services/european-car-repair/audi" replace />} />
                      
                      <Route path="/disclaimer" element={<Disclaimer />} />
                      <Route path="/privacy" element={<Privacy />} />
                      <Route path="/privacy-policy" element={<Privacy />} />
                      <Route path="/refund-policy" element={<RefundPolicy />} />
                      <Route path="/cookie-policy" element={<CookiePolicy />} />
                      <Route path="/terms" element={<TermsAndConditions />} />
                      <Route path="/terms-and-conditions" element={<TermsAndConditions />} />
                      {/* Financing page temporarily disabled - redirects to finance application */}
                      <Route path="/financing" element={<Navigate to="/finance-application" replace />} />
                      <Route path="/trade-in" element={<TradeIn />} />
                      <Route path="/blog" element={<Blog />} />
                      <Route path="/blog/porsche-psm-failure-guide" element={<PorschePSMFailure />} />
                      <Route path="/blog/fix-vw-epc-light" element={<VWEPCLight />} />
                      <Route path="/blog/can-jiffy-lube-change-bmw-oil" element={<BMWOilChangeJiffyLube />} />
                      <Route path="/blog/common-bmw-problems" element={<BMWCommonProblems />} />
                      <Route path="/blog/porsche-cayenne-suspension-dallas" element={<PorscheCayenneSuspensionDallas />} />
                      <Route path="/blog/porsche-cayenne-lifespan" element={<PorscheCayenneLifespan />} />
                      <Route path="/blog/common-car-ac-problems" element={<Navigate to="/services" replace />} />
                      <Route path="/blog/:slug" element={<BlogPost />} />
                      <Route path="/dealers/dallas" element={<LocalSEO />} />
                      <Route path="/ai-tools" element={<AiTools />} />
                      <Route path="/finance-application" element={<FinanceApplication />} />
                      <Route path="*" element={<NotFound />} />
                    </Routes>
                    <FooterNew />
                    {/* Sticky bar removed - contact buttons are in the header */}
                    <ChatBot />
                  </>
                }
              />
            </Routes>
          </AnimatePresence>
        </div>
      </Router>
    </HelmetProvider>
  );
}

export default App;